<!--
  OrderSection.vue — 订单区域（签约记录 + 支付记录 + 退款记录）
-->
<template>
  <div>
    <!-- ==================== 签约订单 ==================== -->
    <div class="uq-order-group">
      <section class="uq-section">
        <div class="uq-section-title">签约记录</div>
        <div
          v-if="ordersLoading"
          class="uq-loading-text"
        >加载中...</div>
        <div
          v-else-if="!agreements.length"
          class="uq-empty-text"
        >暂无签约订单</div>
        <div v-else>
          <div
            v-for="(ag, idx) in agreements"
            :key="idx"
            class="uq-card"
          >
            <div class="uq-card-row"><span class="uq-label">签约渠道</span><span>{{ ag.tradeType }}</span></div>
            <div class="uq-card-row"><span class="uq-label">签约交易类型</span><span>{{ ag.agreementTradeType }}</span></div>
            <div class="uq-card-row">
              <span class="uq-label">签约状态</span>
              <span :class="ag.isAgreement ? 'uq-tag-green' : 'uq-tag-gray'">{{ ag.isAgreement ? '已签约' : '已解约' }}</span>
              <button
                v-if="ag.isAgreement"
                class="uq-unsign-btn"
                :disabled="unsigning === ag.externalAgreementNo"
                @click="handleUnsign(ag)"
              >{{ unsigning === ag.externalAgreementNo ? '解约中...' : '解约' }}</button>
            </div>
            <div class="uq-card-row"><span class="uq-label">签约编号</span><span
                class="uq-mono uq-copyable"
                @click="copyText(ag.externalAgreementNo)"
              >{{ ag.externalAgreementNo }}</span></div>
            <div class="uq-card-row"><span class="uq-label">签约时间</span><span>{{ ag.createTime }}</span></div>
            <div class="uq-card-row"><span class="uq-label">解约时间</span><span>{{ formatOptionalDateTime(ag.unsubscribeTime) }}</span></div>
            <div class="uq-card-row"><span class="uq-label">下次扣款</span><span :style="!ag.isAgreement ? 'text-decoration: line-through; color: #9b9a97;' : ''">{{ ag.nextDeductionDate }}</span></div>
            <div class="uq-card-row"><span class="uq-label">投放媒体</span><span>{{ formatAdMedia(ag.adPackageName, ag.adAppName) }}</span></div>

            <HelibaoAgreementDetails
              v-if="isHelibaoAgreement(ag)"
              :external-agreement-no="ag.externalAgreementNo"
            />
            <KqAgreementDetails
              v-if="isKqAgreement(ag)"
              :external-agreement-no="ag.externalAgreementNo"
            />

            <div
              v-if="ag.paidOrders && ag.paidOrders.length"
              class="uq-sub-section"
            >
              <div class="uq-sub-title">支付订单</div>
              <table class="uq-table">
                <thead>
                  <tr>
                    <th>商品</th>
                    <th>订单号</th>
                    <th>支付次数</th>
                    <th>金额</th>
                    <th>渠道</th>
                    <th>时间</th>
                    <th>退款</th>
                  </tr>
                </thead>
                <tbody>
                  <tr
                    v-for="(order, oi) in ag.paidOrders"
                    :key="oi"
                  >
                    <td>{{ order.title }}</td>
                    <td
                      class="uq-mono uq-copyable"
                      @click="copyText(order.outTradeNo)"
                    >{{ order.outTradeNo }}</td>
                    <td>{{ order.paidCount }}</td>
                    <td>¥{{ (order.amount / 100).toFixed(2) }}</td>
                    <td>{{ order.tradeType }}</td>
                    <td>{{ order.createTime }}</td>
                    <td>
                      <template v-if="hasRefund(order.refundOrder)">
                        <span :class="refundStatusClass(order.refundOrder.refundStatus)">{{ refundStatusText(order.refundOrder.refundStatus) }}</span>
                        <br /><span class="uq-small-text">¥{{ (order.refundOrder.refundAmount / 100).toFixed(2) }} · {{ order.refundOrder.createTime }}</span>
                      </template>
                      <template v-else><span class="uq-tag-gray">未退款</span></template>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </section>
    </div>

    <!-- ==================== 所有订单 ==================== -->
    <div class="uq-order-group">
      <section class="uq-section">
        <div class="uq-section-title">支付记录</div>
        <div
          v-if="allOrdersLoading"
          class="uq-loading-text"
        >加载中...</div>
        <div
          v-else-if="!allOrders.length"
          class="uq-empty-text"
        >暂无支付记录</div>
        <table
          v-else
          class="uq-table"
        >
          <thead>
            <tr>
              <th>订单号</th>
              <th>应用</th>
              <th>支付次数</th>
              <th>金额</th>
              <th>渠道</th>
              <th>类型</th>
              <th>时间</th>
              <th>退款</th>
              <th>操作</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(order, idx) in allOrders"
              :key="idx"
            >
              <td
                class="uq-mono uq-copyable"
                @click="copyText(order.outTradeNo)"
              >{{ order.outTradeNo }}</td>
              <td><span :class="order.appId === 267 ? 'uq-tag-app-special' : ''">{{ order.appName }}</span></td>
              <td>{{ order.paidCount }}</td>
              <td>¥{{ (order.totalFee / 100).toFixed(2) }}</td>
              <td>{{ order.bankType }}</td>
              <td>{{ order.tradeType }}</td>
              <td>{{ order.createTime }}</td>
              <td>
                <template v-if="hasRefund(order.refundOrder)">
                  <span :class="refundStatusClass(order.refundOrder.refundStatus)">{{ refundStatusText(order.refundOrder.refundStatus) }}</span>
                  <br /><span class="uq-small-text">¥{{ (order.refundOrder.refundAmount / 100).toFixed(2) }} · {{ order.refundOrder.createTime }}</span>
                </template>
                <span
                  v-else
                  class="uq-tag-gray"
                >无</span>
              </td>
              <td>
                <button
                  v-if="!hasRefund(order.refundOrder)"
                  class="uq-refund-btn"
                  :disabled="refundingTradeNo === order.outTradeNo"
                  @click="handleRefund(order)"
                >{{ refundingTradeNo === order.outTradeNo ? '退款中...' : '退款' }}</button>
              </td>
            </tr>
          </tbody>
        </table>
      </section>
    </div>

    <!-- ==================== 退款记录 ==================== -->
    <div class="uq-order-group">
      <section class="uq-section">
        <div class="uq-section-title">退款记录</div>
        <div
          v-if="refundOrdersLoading"
          class="uq-loading-text"
        >加载中...</div>
        <div
          v-else-if="!refundOrders.length"
          class="uq-empty-text"
        >暂无退款记录</div>
        <table
          v-else
          class="uq-table"
        >
          <thead>
            <tr>
              <th>付款订单号</th>
              <th>退款金额</th>
              <th>订单总额</th>
              <th>渠道</th>
              <th>后台退款状态</th>
              <th>渠道退款状态</th>
              <th>退款时间</th>
              <th>备注</th>
            </tr>
          </thead>
          <tbody>
            <tr
              v-for="(r, idx) in refundOrders"
              :key="idx"
            >
              <td
                class="uq-mono uq-copyable"
                @click="copyText(r.outTradeNo)"
              >{{ r.outTradeNo }}</td>
              <td>¥{{ (r.refundAmount / 100).toFixed(2) }}</td>
              <td>¥{{ (r.totalAmount / 100).toFixed(2) }}</td>
              <td>{{ r.tradeType }}</td>
              <td><span :class="refundStatusClass(r.status)">{{ refundStatusText(r.status) }}</span></td>
              <td>
                <KqRefundStatus
                  v-if="r.payType === 'kq'"
                  :trade-no="r.outTradeNo"
                />
                <HelibaoRefundStatus
                  v-else-if="r.payType === 'hlb'"
                  :trade-no="r.outTradeNo"
                />
                <span v-else class="uq-small-text">-</span>
              </td>
              <td>{{ r.refundTime }}</td>
              <td class="uq-small-text">{{ r.title }}</td>
            </tr>
          </tbody>
        </table>
      </section>
    </div>

    <!-- ==================== 确认对话框 ==================== -->
    <Teleport to="body">
      <transition name="uq-confirm-fade">
        <div
          v-if="confirmVisible"
          class="uq-confirm-overlay"
          @click.self="cancelConfirm"
        >
          <div class="uq-confirm-box">
            <div class="uq-confirm-title">{{ confirmTitle }}</div>
            <div class="uq-confirm-body">{{ confirmMessage }}</div>
            <div class="uq-confirm-actions">
              <button
                class="uq-confirm-cancel-btn"
                @click="cancelConfirm"
              >取消</button>
              <button
                class="uq-confirm-ok-btn"
                @click="okConfirm"
              >确定</button>
            </div>
          </div>
        </div>
      </transition>
    </Teleport>
  </div>
</template>

<script>
import { formatOptionalDateTime } from "@/utils/formatters";
import { isHelibaoAgreement } from "@/utils/helibaoAgreement";
import { isKqAgreement } from "@/utils/kqAgreement";
import HelibaoAgreementDetails from "@/components/userquery/HelibaoAgreementDetails.vue";
import KqAgreementDetails from "@/components/userquery/KqAgreementDetails.vue";
import KqRefundStatus from "@/components/userquery/KqRefundStatus.vue";
import HelibaoRefundStatus from "@/components/userquery/HelibaoRefundStatus.vue";

export default {
  name: "UserQueryOrderSection",
  components: { HelibaoAgreementDetails, KqAgreementDetails, KqRefundStatus, HelibaoRefundStatus },
  props: {
    ordersLoading: Boolean,
    agreements: Array,
    allOrdersLoading: Boolean,
    allOrders: Array,
    refundOrdersLoading: Boolean,
    refundOrders: Array,
  },
  emits: ["unsign", "refund", "toast"],
  data() {
    return {
      unsigning: "",
      refundingTradeNo: "",
      confirmVisible: false,
      confirmTitle: "",
      confirmMessage: "",
      confirmResolve: null,
    };
  },
  methods: {
    formatOptionalDateTime,
    isHelibaoAgreement,
    isKqAgreement,
    formatAdMedia(packageName, appName) {
      const normalizedPackageName =
        typeof packageName === "string" ? packageName.trim() : "";
      const normalizedAppName =
        typeof appName === "string" ? appName.trim() : "";
      if (normalizedPackageName && normalizedAppName) {
        return `${normalizedPackageName}(${normalizedAppName})`;
      }
      return normalizedPackageName || normalizedAppName || "-";
    },
    hasRefund(refundOrder) {
      if (!refundOrder || typeof refundOrder !== "object") return false;
      return (
        Object.keys(refundOrder).length > 0 &&
        (refundOrder.refundStatus ||
          refundOrder.outRefundNo ||
          refundOrder.refundAmount)
      );
    },
    copyText(text) {
      navigator.clipboard.writeText(text).then(() => {
        this.$emit("toast", "已复制: " + text);
      });
    },
    refundStatusText(status) {
      return (
        {
          SUCCESS: "退款申请成功",
          CLOSED: "退款关闭",
          PROCESSING: "退款处理中",
          ABNORMAL: "退款异常",
          FAILED: "退款失败",
        }[status] || status
      );
    },
    refundStatusClass(status) {
      return (
        {
          SUCCESS: "uq-tag-green",
          CLOSED: "uq-tag-gray",
          PROCESSING: "uq-tag-blue",
          ABNORMAL: "uq-tag-red",
          FAILED: "uq-tag-red",
        }[status] || "uq-tag-gray"
      );
    },
    showConfirm(title, message) {
      return new Promise((resolve) => {
        this.confirmTitle = title;
        this.confirmMessage = message;
        this.confirmResolve = resolve;
        this.confirmVisible = true;
      });
    },
    okConfirm() {
      this.confirmVisible = false;
      if (this.confirmResolve) this.confirmResolve(true);
    },
    cancelConfirm() {
      this.confirmVisible = false;
      if (this.confirmResolve) this.confirmResolve(false);
    },
    async handleUnsign(ag) {
      const ok = await this.showConfirm(
        "解约确认",
        `确定要解约【${ag.externalAgreementNo}】吗？`
      );
      if (!ok) return;
      this.unsigning = ag.externalAgreementNo;
      this.$emit("unsign", ag.externalAgreementNo, () => {
        this.unsigning = "";
      });
    },
    async handleRefund(order) {
      const ok = await this.showConfirm(
        "退款确认",
        `确定要退款订单【${order.outTradeNo}】吗？`
      );
      if (!ok) return;
      this.refundingTradeNo = order.outTradeNo;
      this.$emit("refund", order.outTradeNo, () => {
        this.refundingTradeNo = "";
      });
    },
  },
};
</script>

<style scoped>
.uq-order-group {
  background: #f8f7f5;
  border: 1px solid #e8e5e0;
  border-radius: 6px;
  padding: 20px 20px 0;
  margin-bottom: 24px;
}
.uq-section {
  margin-bottom: 20px;
}
.uq-section-title {
  font-size: 16px;
  font-weight: 600;
  color: #37352f;
  margin-bottom: 12px;
  padding-bottom: 6px;
  border-bottom: 1px solid #e8e5e0;
}
.uq-loading-text,
.uq-empty-text {
  font-size: 14px;
  color: #9b9a97;
  padding: 12px 0;
}
.uq-card {
  background: #fbfbfa;
  border: 1px solid #e8e5e0;
  border-radius: 4px;
  padding: 16px;
  margin-bottom: 12px;
}
.uq-card-row {
  display: flex;
  align-items: center;
  padding: 4px 0;
  font-size: 14px;
}
.uq-label {
  width: 110px;
  flex-shrink: 0;
  color: #9b9a97;
  font-size: 13px;
}
.uq-sub-section {
  margin-top: 12px;
  padding-top: 12px;
  border-top: 1px solid #e8e5e0;
}
.uq-sub-title {
  font-size: 13px;
  font-weight: 600;
  color: #9b9a97;
  margin-bottom: 8px;
}
.uq-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}
.uq-table th {
  text-align: left;
  padding: 8px 10px;
  font-weight: 500;
  color: #9b9a97;
  border-bottom: 1px solid #e8e5e0;
  white-space: nowrap;
}
.uq-table td {
  padding: 8px 10px;
  border-bottom: 1px solid #f0efed;
  vertical-align: top;
}
.uq-table tr:hover td {
  background: #fbfbfa;
}
.uq-mono {
  font-family: "SFMono-Regular", Menlo, Consolas, monospace;
  font-size: 12px;
}
.uq-copyable {
  cursor: pointer;
  transition: background 0.15s;
  border-radius: 2px;
  padding: 1px 3px;
}
.uq-copyable:hover {
  background: #d3e5ef;
}
.uq-small-text {
  font-size: 11px;
  color: #9b9a97;
}
.uq-tag-green {
  background: #dbeddb;
  color: #2d6a2e;
  padding: 2px 8px;
  border-radius: 3px;
  font-size: 12px;
}
.uq-tag-red {
  background: #fbe4e4;
  color: #c4554d;
  padding: 2px 8px;
  border-radius: 3px;
  font-size: 12px;
}
.uq-tag-blue {
  background: #d3e5ef;
  color: #2878b5;
  padding: 2px 8px;
  border-radius: 3px;
  font-size: 12px;
}
.uq-tag-gray {
  background: #e8e5e0;
  color: #6b6966;
  padding: 2px 8px;
  border-radius: 3px;
  font-size: 12px;
}
.uq-tag-app-special {
  background: #daeaf6;
  color: #1a5f8a;
  padding: 2px 8px;
  border-radius: 3px;
  font-size: 12px;
}
.uq-unsign-btn {
  margin-left: 8px;
  padding: 2px 10px;
  font-size: 12px;
  color: #c4554d;
  background: #fbe4e4;
  border: 1px solid #e8c4c4;
  border-radius: 3px;
  cursor: pointer;
}
.uq-unsign-btn:hover {
  background: #f5d0d0;
}
.uq-unsign-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.uq-refund-btn {
  padding: 2px 10px;
  font-size: 12px;
  color: #d9730d;
  background: #fdecc8;
  border: 1px solid #e8d5b0;
  border-radius: 3px;
  cursor: pointer;
}
.uq-refund-btn:hover {
  background: #f9dda5;
}
.uq-refund-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}
.uq-confirm-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 300;
}
.uq-confirm-box {
  background: #fff;
  border-radius: 8px;
  padding: 24px 28px;
  min-width: 340px;
  max-width: 440px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.18);
}
.uq-confirm-title {
  font-size: 16px;
  font-weight: 600;
  color: #37352f;
  margin-bottom: 12px;
}
.uq-confirm-body {
  font-size: 14px;
  color: #55534e;
  line-height: 1.6;
  margin-bottom: 20px;
  word-break: break-all;
}
.uq-confirm-actions {
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}
.uq-confirm-cancel-btn {
  padding: 6px 18px;
  font-size: 13px;
  color: #37352f;
  background: #f0efed;
  border: 1px solid #e8e5e0;
  border-radius: 4px;
  cursor: pointer;
}
.uq-confirm-cancel-btn:hover {
  background: #e8e5e0;
}
.uq-confirm-ok-btn {
  padding: 6px 18px;
  font-size: 13px;
  color: #fff;
  background: #c4554d;
  border: none;
  border-radius: 4px;
  cursor: pointer;
}
.uq-confirm-ok-btn:hover {
  background: #b04840;
}
.uq-confirm-fade-enter-active,
.uq-confirm-fade-leave-active {
  transition: opacity 0.2s;
}
.uq-confirm-fade-enter-from,
.uq-confirm-fade-leave-to {
  opacity: 0;
}
</style>
